import { useState, useMemo } from "react";
import { TablePaginationConfig } from "antd/lib/table";

interface UseTablePaginationProps {
  dataLength: number;
  height: number;
  autoPageSize?: boolean;
  defaultPageSize?: number;
  hideOnSinglePage?: boolean;
  showSizeChanger?: boolean;
  style?: React.CSSProperties;
}

interface UseTablePaginationResult {
  current: number;
  pageSize: number;
  total: number;
  items: number;
  pagination: TablePaginationConfig;
  setCurrent: (page: number) => void;
  setPageSize: (size: number) => void;
}

export function useTablePagination({
  dataLength,
  height,
  autoPageSize = true,
  defaultPageSize = 20,
  hideOnSinglePage = false,
  showSizeChanger = true,
  style = { margin: "8px 0 0", overflow: "visible" },
}: UseTablePaginationProps): UseTablePaginationResult {
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  // Calculate page size based on height if autoPageSize is enabled
  const calculatedPageSize = useMemo(() => {
    if (!autoPageSize) return pageSize;
    return Math.floor((height - (29 + 33)) / 37); // th + paging / row height
  }, [height, autoPageSize, pageSize]);

  const total = dataLength;
  const skip = (current - 1) * calculatedPageSize;
  const items = Math.min(calculatedPageSize, total - skip);

  const pagination: TablePaginationConfig = {
    current,
    total,
    pageSize: calculatedPageSize,
    size: "small",
    style: { ...style, height: 25 },
    hideOnSinglePage,
    showSizeChanger,
    onChange: (page, size) => {
      setCurrent(page);
      if (size) setPageSize(size);
    },
  };

  return {
    current,
    pageSize: calculatedPageSize,
    total,
    items,
    pagination,
    setCurrent,
    setPageSize,
  };
}
